import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  Table,
  TableColumn,
  TableProps,
  FavoriteToggleIcon,
} from '@backstage/core-components';
import {
  getEntityRelations,
  humanizeEntityRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import Typography from '@material-ui/core/Typography';
import { visuallyHidden } from '@mui/utils';
import Edit from '@material-ui/icons/Edit';
import OpenInNew from '@material-ui/icons/OpenInNew';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { CatalogTableColumnsFunc, CatalogTableRow } from './types';
import { defaultCatalogTableColumnsFunc } from './defaultCatalogTableColumnsFunc';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import { CatalogTableToolbar } from './CatalogTableToolbar';
import { Grid, TablePagination } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { getUniqueTags } from '../../utils/helpers';
import InfoCard from '../InfoCard/InfoCard';
import { useAppDispatch } from '../../redux/hooks';
import { setSavedUrl } from '../../redux/catalogTableSlice';

export interface CatalogTableProps extends TableProps<CatalogTableRow> {
  // isLoading prop is removed as it's no longer needed for this fix
  columns?: TableColumn<CatalogTableRow>[] | CatalogTableColumnsFunc;
  emptyContent?: ReactNode;
  subtitle?: string;
  selectedTab?: number;
  headerDisplayable?: boolean;
  hasDetails: boolean;
  entities?: CatalogTableRow[];
  showSearchBar?: boolean;
  showActionsColumn?: boolean;
  showRecordCount?: boolean;
  forceTitle?: boolean;
  maxHeight?: string;
  overflowY?: string;
  overflow?: string;
  addBorder?: string;
  pageSize?: number;
  pageSizeOptions?: number[];
  fitToScreen?: boolean;
}

const useStyles = makeStyles(theme => ({
  pagination: {
    [theme.breakpoints.down('sm')]: {
      '& .MuiTablePagination-toolbar': {
        padding: '0 4px',
        minHeight: '48px',
      },
      '& .MuiTablePagination-selectLabel': {
        margin: 0,
        padding: '0 4px',
      },
      '& .MuiTablePagination-select': {
        margin: 0,
        paddingLeft: '2px',
        paddingRight: '16px',
      },
      '& .MuiTablePagination-displayedRows': {
        margin: 0,
        padding: '0 4px',
      },
      '& .MuiTablePagination-actions': {
        marginLeft: '0px',
      },
    },
  },
  table: {
    flexDirection: 'column',
    height: '100%',
    fontSize: theme.breakpoints.down(768) ? 'small' : 'medium',
    boxShadow: '0 4px 20px rgba(15, 15, 15, 0.08)',
    border: `1px solid ${(theme.palette as any).border || '#e6e6e6'}`,
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #eeeeee',
      padding: '11px 16px',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      textTransform: 'uppercase',
      letterSpacing: '0.045em',
      fontWeight: 700,
      fontSize: '0.72rem',
      color: '#5a5a5a',
      backgroundColor: '#fafafa',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'nowrap',
    },
    '& tbody .MuiTableRow-root': {
      transition: 'background-color 0.15s ease',
    },
    '& tbody .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: 'rgba(0, 0, 0, 0.018)',
    },
    '& tbody .MuiTableRow-root:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '& ::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '& ::-webkit-scrollbar-track': {
      background: theme.palette.background.paper,
      borderRadius: '4px',
    },
    '& ::-webkit-scrollbar-thumb': {
      background: theme.palette.secondary.main,
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.secondary.light,
      },
      borderColor: theme.palette.background.paper,
    },
  },
  tableHeaderStyle: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#f5f5f5',
    zIndex: 100,
  },
  integratedWidget: {
    textAlign: 'center',
    fontWeight: 400,
    fontSize: 'medium',
  },
  cardsCollection: {
    display: 'flex',
    padding: '1%',
    gap: '2%',
    textAlign: 'center',
    marginBottom: '20px',
  },
  grid: {
    backgroundColor: theme.palette.background.default,
  },
  root: {
    flexGrow: 1,
    marginBottom: '10px',
  },
  header: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: theme.spacing(2),
    borderRadius: '3px 3px 0 0',
    textAlign: 'center' as const,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: '17px',
    fontFamily: 'Helvetica Neue, Helvetica, Roboto, Arial, sans-serif',
  },
  paper: {
    padding: theme.spacing(2),
    borderRadius: 0,
    textAlign: 'center' as const,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '25px',
  },
  widget: {
    flexGrow: 1,
    flexShrink: 1,
    padding: '0rem',
    width: '100%',
    height: '100%',
    overflow: 'auto',
    display: 'flex',
    justifyItems: 'stretch',
  },
  toolbarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 111,
    borderBottom: 'none',
    margin: 0,
    padding: '8px 0',
    marginBottom: '-1px',
    // backgroundColor: (theme.palette as any).navigation?.background || '#000000',
    // backgroundRepeat: 'no-repeat',
    borderRadius: '5px 5px 0 0',
    // color: (theme.palette as any).navigation?.color || '#ffffff',
  },
  recordCount: {
    marginLeft: '10px',
    fontSize: '0.9rem',
    fontWeight: 500,
    color: (theme.palette as any).textSubtle || theme.palette.text.secondary,
  },
}));

const ROW_HEIGHT_PX = 52;

// Rows that fit the current viewport, leaving room for the header bar, toolbar and pagination.
const computeFitRows = (): number => {
  if (typeof window === 'undefined') {
    return 10;
  }
  const reservedPx = 260;
  const available = window.innerHeight - reservedPx;
  return Math.max(5, Math.floor(available / ROW_HEIGHT_PX));
};

export const CustomCatalogTable = (props: CatalogTableProps) => {
  const {
    columns = defaultCatalogTableColumnsFunc,
    tableOptions,
    subtitle,
    emptyContent,
    onRowClick,
    headerDisplayable = true,
    hasDetails = false,
    entities = [],
    showSearchBar = true,
    showActionsColumn = true,
    showRecordCount = false,
    forceTitle = false,
    maxHeight = 'auto',
    overflowY = 'auto',
    overflow = 'auto',
    addBorder = '',
    fitToScreen = false,
    selectedTab,
  } = props;
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const dispatch = useAppDispatch();

  const uniqueTags = getUniqueTags(entities);
  const externalConsumers = uniqueTags.size;
  const toTitleCase = (str: string): string => {
    if (!str) {
      return '';
    }
    const isAcronym = (word: string) => {
      return word.length > 1 && word === word.toUpperCase();
    };
    return str
      .split(/(\s+|-)/)
      .map(word => {
        if (word.match(/^\s+$/) || word === '-') {
          return word;
        }
        if (isAcronym(word)) {
          return word;
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
      })
      .join('');
  };
  const tableColumns = useMemo(() => {
    if (typeof columns === 'function') {
      return columns({ entities });
    }
    return (
      columns?.map(column => ({
        ...column,
        field: column.field || column.title,
      })) || []
    );
  }, [columns, entities]);

  const { t } = useTranslationRef(catalogTranslationRef);
  const defaultActions: TableProps<CatalogTableRow>['actions'] =
    showActionsColumn
      ? [
          ({ entity }) => {
            const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
            const titleText = t('catalogTable.viewActionTitle');

            return {
              icon: () => (
                <>
                  <Typography style={visuallyHidden}>{titleText}</Typography>
                  <OpenInNew fontSize="small" />
                </>
              ),
              tooltip: titleText,
              disabled: !url,
              onClick: () => {
                if (!url) return;
                window.open(url, '_blank');
              },
            };
          },
          ({ entity }) => {
            const url = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
            const titleText = t('catalogTable.editActionTitle');

            return {
              icon: () => (
                <>
                  <Typography style={visuallyHidden}>{titleText}</Typography>
                  <Edit fontSize="small" />
                </>
              ),
              tooltip: titleText,
              disabled: !url,
              onClick: () => {
                if (!url) return;
                window.open(url, '_blank');
              },
            };
          },
          ({ entity }) => {
            const isStarred = isStarredEntity(entity);
            const titleText = isStarred
              ? t('catalogTable.unStarActionTitle')
              : t('catalogTable.starActionTitle');

            return {
              cellStyle: { paddingLeft: '1em' },
              icon: () => <FavoriteToggleIcon isFavorite={isStarred} />,
              tooltip: titleText,
              onClick: () => toggleStarredEntity(entity),
            };
          },
        ]
      : [];

  const title = headerDisplayable || forceTitle ? props.title : undefined;
  const actions = useMemo(
    () => props.actions || defaultActions,
    [props.actions, defaultActions],
  );
  const rows = useMemo(() => entities.map(toEntityRow), [entities]);
  const annotation_fields = ['serviceOwner', 'team', 'techLead', 'productOwner', 'apiOwnership', 'serviceCategory', 'serviceScope', 'dmpDomain', 'serviceNowAssignmentGroup', 'PII', 'trouxService'];
   rows.forEach(
    row => {
      row.entity.metadata.title = toTitleCase(
        row.entity.metadata.title || '',
      );
      
      if (row.entity.metadata.annotations) {
        const annotations = row.entity.metadata.annotations;
        
        annotation_fields.forEach(field => {
          const key = `backstage.io/${field}`;
          if (annotations[key]) {
            annotations[key] = toTitleCase(annotations[key]);
          }
        });
      }
});
  const defaultPageSize = props.pageSize || 10;
  const defaultPageSizeOptions = props.pageSizeOptions || [10, 20, 50, 100];
  const classes = useStyles();
  const theme = useTheme();

  const [fitRows, setFitRows] = useState<number>(() => computeFitRows());
  useEffect(() => {
    if (!fitToScreen) {
      return undefined;
    }
    const handleResize = () => setFitRows(computeFitRows());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fitToScreen]);

  const effectivePageSize = fitToScreen ? fitRows : defaultPageSize;
  const effectivePageSizeOptions = fitToScreen
    ? Array.from(new Set([fitRows, 20, 50, 100])).sort((a, b) => a - b)
    : defaultPageSizeOptions;
  const bodyMaxHeight = fitToScreen
    ? `${fitRows * ROW_HEIGHT_PX}px`
    : entities.length > defaultPageSize
      ? `${defaultPageSize * 53}px`
      : 'auto';

  useEffect(() => {
    const pathname = window.location.pathname;
    const search = window.location.search;
    dispatch(setSavedUrl(`${pathname}${search}`));
  }, [dispatch]);

  return (
    <div className={classes.integratedWidget}>
      {!headerDisplayable && (
        <div className={classes.cardsCollection}>
          <Grid container spacing={2} className={classes.grid}>
            <InfoCard title="Services/APIs" value={entities.length} />
            <div style={{ flexGrow: !hasDetails ? 0.1 : 0 }} />
            {!hasDetails && (
              <InfoCard title="External Consumers" value={externalConsumers} />
            )}
          </Grid>
        </div>
      )}

      <div className={classes.table}>
        <Table<CatalogTableRow>
          key={effectivePageSize}
          isLoading={false}
          columns={tableColumns}
          options={{
            paging: true,
            pageSize: effectivePageSize,
            pageSizeOptions: effectivePageSizeOptions,
            emptyRowsWhenPaging: false,
            maxBodyHeight: bodyMaxHeight,
            headerStyle: {
              position: 'sticky',
              top: 0,
              backgroundColor: '#fafafa',
              color: '#5a5a5a',
              zIndex: 100,
              fontSize: 'small',
              borderBottom: '1px solid #e0e0e0',
            },
            ...tableOptions,
          }}
          components={{
            Pagination: ({ page, rowsPerPage, rowsPerPageOptions, onPageChange, onRowsPerPageChange }) => (
              <TablePagination
                count={entities.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={rowsPerPageOptions}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                className={classes.pagination}
                component="div"
              />
            ),
            Toolbar: props => (
              <div
                className={classes.toolbarContainer}
                style={{ backgroundColor: theme.palette.background.paper }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    textAlign: 'left',
                    width: '100%',
                  }}
                >
                  {(title || subtitle) && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        gap: '8px',
                        paddingLeft: '10px',
                      }}
                    >
                      {title && (
                        <Typography
                          component="span"
                          style={{
                            fontWeight: 700,
                            fontSize: '20px',
                            color: '#000000',
                          }}
                        >
                          {title}
                          {showRecordCount && entities.length > 0 && (
                            <span className={classes.recordCount}>
                              ({entities.length})
                            </span>
                          )}
                        </Typography>
                      )}
                      {subtitle && (
                        <Typography
                          component="span"
                          style={{
                            fontWeight: 500,
                            fontSize: '16px',
                            color: '#666666',
                          }}
                        >
                          {subtitle}
                        </Typography>
                      )}
                    </div>
                  )}
                  <hr
                    style={{
                      width: '100%',
                      border: 'none',
                      borderTop: '1px solid #e0e0e0',
                      margin: '8px 0 0 0',
                    }}
                  />
                </div>
                {showSearchBar && <CatalogTableToolbar {...toolbarProps} />}
              </div>
            ),
          }}
          title={undefined}
          data={rows}
          actions={actions}
          subtitle={undefined}
          emptyContent={emptyContent}
          onRowClick={onRowClick}
        />
      </div>
    </div>
  );
};

CustomCatalogTable.defaultColumnsFunc = defaultCatalogTableColumnsFunc;
function toEntityRow(entity: any): CatalogTableRow {
  const partOfSystemRelations = getEntityRelations(entity, 'partOf', {
    kind: 'system',
  });

  const ownedByRelations = getEntityRelations(entity, 'ownedBy');

  const entityName = entity?.metadata?.name ?? '';
  const nameIsSafe = typeof entityName === 'string';

  const shouldIncrementRelation =
    entity?.relations?.some((relation: { type: string }) =>
      ['dependsOn', 'dependencyOf', 'apiConsumedBy'].includes(relation?.type),
    ) &&
    nameIsSafe &&
    !entityName.toLocaleLowerCase().includes('pfm') &&
    !entityName.toLocaleLowerCase().includes('-db');

  const hasValidRefFields =
    typeof entity?.kind === 'string' &&
    typeof entity?.metadata?.name === 'string' &&
    typeof entity?.metadata?.namespace === 'string';

  const safeId = hasValidRefFields
    ? stringifyEntityRef(entity)
    : `invalid-ref-${Math.random().toString(36).substr(2, 6)}`;

  return {
    id: entity?.metadata?.uid || safeId,
    entity,
    relationCount:
      (entity?.relationCount ?? 0) + (shouldIncrementRelation ? 1 : 0),
    resolved: {
      name: entity?.metadata?.name || 'Unknown',
      entityRef: hasValidRefFields
        ? stringifyEntityRef(entity)
        : 'Unknown Entity Ref',
      ownedByRelationsTitle: ownedByRelations
        .map(r => humanizeEntityRef(r, { defaultKind: 'group' }))
        .join(', '),
      ownedByRelations,
      partOfSystemRelationTitle: partOfSystemRelations
        .map(r => humanizeEntityRef(r, { defaultKind: 'system' }))
        .join(', '),
      partOfSystemRelations,
    },
  };
}
